import { getAll, update } from "@/actions/setting.action";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Box, Typography, Grid } from "@mui/material";
import SettingField from "./SettingField";
import PaymentMethodsSetting from "./PaymentMethodsSetting"; // Özel bileşen
import { logger } from "@/lib/default-logger";
import PackagesSetting from "./PackagesSetting";

const categorizedKeys = {
  paymentMethods: PaymentMethodsSetting, // Özel bileşen
  packages: PackagesSetting,
};

export default function GeneralSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { settings, status } = useSelector((state: any) => state.settings);
  const [loading, setLoading] = useState(true);
  const [editedSettings, setEditedSettings] = useState<any>({});

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      if (isMounted) {
        dispatch(getAll() as any);
        setTimeout(() => setLoading(false), 1000);
      }
    };

    fetchSettings();

    return () => {
      isMounted = false;
    };
  }, [dispatch, status]);

  const handleChange = (key: string, value: any) => {
    logger.log(key, value);

    setEditedSettings((prev: any) => {
      let newValue = value;

      // Eğer JSON string olarak geliyorsa, parse et
      if (typeof newValue === "string") {
        try {
          newValue = JSON.parse(newValue);
        } catch (e) {
          logger.warn("Invalid JSON format, keeping as string.");
        }
      }

      // 🔥 Derin kopyalama ile yeni state oluşturuyoruz!
      return { ...prev, [key]: newValue };
    });
  };

  const handleNestedChange = (
    key: string,
    index: number,
    field: string,
    value: any
  ) => {
    setEditedSettings((prev: any) => {
      const currentSettings =
        prev[key] || settings.find((s: any) => s.key === key)?.value || {};
      return {
        ...prev,
        [key]: {
          ...JSON.parse(JSON.stringify(currentSettings)), // Derin kopyalama
          [field]: value,
        },
      };
    });
  };

  const handleAddItem = (key: string, newItem: any) => {
    setEditedSettings((prev: any) => {
      const existingArray =
        prev[key] || settings.find((s: any) => s.key === key)?.value || [];

      // Eğer dizi nesneler içeriyorsa, varsayılan bir boş nesne ekle
      let defaultItem = newItem;

      if (
        Array.isArray(existingArray) &&
        typeof existingArray[0] === "object"
      ) {
        defaultItem = Object.keys(existingArray[0]).reduce(
          (acc: any, field: string) => {
            acc[field] = ""; // Tüm alanlara boş string ata
            return acc;
          },
          {}
        );
      }

      // Eğer dizi düz stringler içeriyorsa, varsayılan bir string ekle
      if (
        Array.isArray(existingArray) &&
        typeof existingArray[0] === "string"
      ) {
        defaultItem = "(000) 000 00 00"; // Varsayılan telefon formatı
      }

      const updatedArray = [...existingArray, defaultItem];

      return {
        ...prev,
        [key]: updatedArray,
      };
    });
  };

  const handleRemoveItem = (key: string, index: number) => {
    setEditedSettings((prev: any) => {
      const updatedArray = [
        ...(prev[key] || settings.find((s: any) => s.key === key)?.value),
      ];
      updatedArray.splice(index, 1);
      return { ...prev, [key]: updatedArray };
    });
  };

  const handleSave = (key: string) => {
    if (!editedSettings[key]) {
      logger.warn(`No changes detected for setting: ${key}`);
      return;
    }

    const setting = settings.find((setting) => setting.key === key);
    if (!setting) {
      logger.error(`Setting not found for key: ${key}`);
      return;
    }

    let finalValue = editedSettings[key];

    // Eğer JSON obje dizisi ise, string olarak kaydet
    // if (Array.isArray(finalValue)) {
    //   finalValue = JSON.stringify(finalValue); // JSON formatına çevir
    // }

    logger.log(`Saving setting [${key}]:`, finalValue);

    dispatch(
      update(setting._id, {
        value: finalValue, // API'ye string olarak gönder
      }) as any
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-4 bg-white rounded">
      <Typography variant="h4" gutterBottom>
        {t("general_settings")}
      </Typography>

      <Grid container spacing={3}>
        {settings.map((setting: any) => {
          const CustomComponent = categorizedKeys[setting.key] || SettingField;
          return (
            <Grid item xs={12} md={12} key={setting._id}>
              <CustomComponent
                setting={setting}
                editedSettings={editedSettings}
                handleChange={handleChange}
                handleNestedChange={handleNestedChange}
                handleAddItem={handleAddItem}
                handleRemoveItem={handleRemoveItem}
                handleSave={handleSave}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
